// ============================================================================
// MÓDULO: SOBRE MIM (VERSÃO 11.4 - INTEGRITY FIXED)
// ARQUIVO: src/components/AboutMe.jsx
// OBJETIVO: Slider 3x1 (Wide), Manifesto 1x1 (Full Text) e Storage Path.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { ASSETS } from '../config/Config';
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import MissionValues from './MissionValues';

// --- CONFIGURAÇÃO DA PASTA DE IMAGENS ---
const STORAGE_PATH = 'Public/fotos_dogs'; 

// --- SUB-COMPONENTE 1: PHOTO SLIDER (MOTOR AUTOMÁTICO) ---
const PhotoSlider = ({ images, interval = 4000 }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (!images || images.length <= 1) return;
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % images.length);
        }, interval);
        return () => clearInterval(timer);
    }, [images, interval]);

    return (
        <div className="absolute inset-0 z-0">
            {images.map((img, idx) => (
                <div
                    key={idx}
                    // bg-cover garante que a imagem cubra todo o container sem bordas brancas
                    className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out ${
                        idx === currentIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{ backgroundImage: `url(${img})` }}
                />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/60 to-transparent opacity-90"></div>
        </div>
    );
};

// --- SUB-COMPONENTE 2: BENTO ITEM ---
const BentoItem = ({ 
    className, 
    title, 
    subtitle, 
    icon, 
    children, 
    image, 
    slideshow, 
    interval, 
    accentColor = "border-gray-800",
    scrollable = false,
    alignBottom = false 
}) => (
    <div className={`relative overflow-hidden rounded-3xl p-6 flex flex-col transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl group ${className} ${image || slideshow ? 'text-white' : 'bg-[#1E293B] border border-gray-700 text-gray-200'}`}>
        
        {slideshow ? (
            <PhotoSlider images={slideshow} interval={interval} />
        ) : image ? (
            <>
                {/* bg-cover aqui também para imagens estáticas */}
                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url(${image})` }}></div>
                <div className={`absolute inset-0 bg-gradient-to-t from-[#0F172A] ${alignBottom ? 'via-[#0F172A]/80' : 'via-[#0F172A]/60'} to-transparent opacity-90`}></div>
            </>
        ) : null}

        <div className="relative z-10 flex justify-between items-start mb-4 flex-shrink-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg backdrop-blur-md border ${accentColor} ${image || slideshow ? 'bg-white/10 text-white' : 'bg-[#0F172A] text-[#C5A059]'}`}>
                <i className={`fas ${icon}`}></i>
            </div>
            {subtitle && <span className="text-[10px] font-bold uppercase tracking-widest opacity-60 border border-white/20 px-2 py-1 rounded-full bg-black/20 backdrop-blur">{subtitle}</span>}
        </div>

        <div className={`relative z-10 flex-1 flex flex-col ${alignBottom ? 'justify-end' : ''} ${scrollable ? 'overflow-y-auto custom-scrollbar pr-2' : ''}`}>
            <div>
                <h3 className={`text-2xl font-serif font-bold mb-3 ${image || slideshow ? 'text-white' : 'text-[#C5A059]'}`}>
                    {title}
                </h3>
                <div className="text-sm leading-relaxed opacity-90 font-light text-justify space-y-3">
                    {children}
                </div>
            </div>
        </div>
    </div>
);

// --- COMPONENTE PRINCIPAL ---
export const AboutMe = () => {
    
    const [dogPhotos, setDogPhotos] = useState([]);
    const [dogPhotosDiag, setDogPhotosDiag] = useState(null);
    
    const FALLBACK_PHOTOS = [
        "https://images.unsplash.com/photo-1543466835-00a7907e9de1?auto=format&fit=crop&q=80&w=800",
        "https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?auto=format&fit=crop&q=80&w=800"
    ];

    useEffect(() => {
        const fetchPhotos = async () => {
            try {
                const storage = getStorage(); 
                const listRef = ref(storage, STORAGE_PATH);
                const res = await listAll(listRef);
                
                if (res.items.length === 0) {
                    console.log(`Pasta '${STORAGE_PATH}' vazia ou não encontrada. Usando fallback.`);
                    setDogPhotosDiag({
                        mode: 'fallback',
                        reason: 'EMPTY_LIST',
                        message: `Sem itens em '${STORAGE_PATH}' (ou sem permissão de listar).`
                    });
                    setDogPhotos(FALLBACK_PHOTOS);
                    return;
                }

                const urls = await Promise.all(res.items.map((itemRef) => getDownloadURL(itemRef)));
                // SHUFFLE ALGORITHM
                const shuffled = urls.sort(() => 0.5 - Math.random());
                setDogPhotos(shuffled);
                setDogPhotosDiag({ mode: 'storage', count: shuffled.length });
            } catch (error) {
                console.error("Erro ao buscar fotos:", error);
                setDogPhotosDiag({
                    mode: 'fallback',
                    reason: error?.code || 'ERROR',
                    message: String(error?.message || error)
                });
                setDogPhotos(FALLBACK_PHOTOS);
            }
        };
        fetchPhotos();
    }, []);

    return (
        <div className="bg-[#0F172A] min-h-screen font-sans py-20 px-6 overflow-hidden relative">
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>

            <div className="container mx-auto max-w-[1400px] relative z-10">
                {/* HERO TITLE */}
                <div className="text-center mb-16 space-y-4">
                    <span className="inline-block px-4 py-1 rounded-full border border-[#C5A059] text-[#C5A059] text-[10px] font-bold uppercase tracking-[0.3em]">
                        Perfil Profissional
                    </span>
                    <h1 className="text-5xl md:text-7xl font-serif font-bold text-white tracking-tight">
                        Estratégia. <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C5A059] to-yellow-200">Harmonia.</span> Justiça.
                    </h1>
                    <p className="text-gray-400 max-w-2xl mx-auto text-lg font-light">
                        Advocacia de alta precisão forjada na disciplina militar, na visão do enxadrista e na sensibilidade do artista.
                    </p>
                </div>

                {/* BENTO GRID (TETRIS LAYOUT) */}
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 auto-rows-[minmax(200px,auto)]">
                    
                    {/* A. O ADVOGADO (2x2) */}
                    <BentoItem 
                        className="md:col-span-2 md:row-span-2 min-h-[500px]" 
                        title="O Advogado & O Professor"
                        subtitle="OAB/PR 113.938"
                        icon="fa-scale-balanced"
                        image={ASSETS.bannerHero}
                        accentColor="border-[#C5A059]"
                        scrollable={true} 
                        alignBottom={true} 
                    >
                        <p>Minha história é um tabuleiro onde cada peça tem seu propósito. Filho de militar, cresci sob uma educação rígida e o exemplo de um pai cuja vida foi um exercício de <strong>estoicismo</strong> — uma serenidade e firmeza que hoje também busco representar. Aprendi a ter firmeza, a não aceitar o inaceitável e a manter a coragem inabalável de lutar pelos meus ideais, custe o que custar.</p>
                        <p>Mas minha base não é apenas lógica; é também humana e intelectual. Sou formado em Letras há quase 25 anos e atuo como professor há quase 30 anos. Na verdade, <strong>antes de ser advogado, sou professor</strong>.</p>
                        <p>Procuro entender e educar as pessoas a respeito de cada processo e de cada decisão que elas pretendem tomar juridicamente. Essa vivência em sala de aula me permite trazer para a advocacia uma clareza didática única: não apenas leio a lei, eu a decodifico. Aplico uma <strong>verdadeira engenharia reversa</strong> para desconstruir a lógica por trás de cada decisão judicial e construir a melhor defesa.</p>
                    </BentoItem>

                    {/* B. O ENXADRISTA (1x1) */}
                    <BentoItem 
                        className="md:col-span-1 md:row-span-1 bg-gradient-to-b from-[#1E293B] to-[#0F172A]"
                        title="O Grande Mestre"
                        subtitle="Mundial Hungria '94"
                        icon="fa-chess-knight"
                        scrollable={true}
                    >
                        <p>A mesma disciplina que moldou meu caráter militar encontrou seu reflexo no xadrez, jogo que aprendi com <strong>meu pai — meu grande técnico no tabuleiro e na vida</strong>.</p>
                        <p>Fui <strong>Campeão Paranaense</strong> e <strong>Vice-Campeão Brasileiro</strong> em categorias de base, culminando na honra de representar o Brasil no <strong>Mundial em Szeged, na Hungria, em 1994</strong>.</p>
                        <p>Lá se vão mais de 30 anos dessa experiência que me ensinou a antecipar cenários, calcular riscos e pensar várias jogadas à frente — uma visão estratégica que hoje aplico em cada processo.</p>
                        <div className="mt-auto pt-6 opacity-20 flex justify-center"><i className="fas fa-chess text-8xl text-[#C5A059]"></i></div>
                    </BentoItem>

                    {/* C. A CRIATIVIDADE (1x1) */}
                    <BentoItem 
                        className="md:col-span-1 md:row-span-1 bg-[#1E293B]"
                        title="Criatividade"
                        icon="fa-guitar"
                        subtitle="Músico & Atleta"
                        scrollable={true}
                    >
                        <p>Para quem vê o advogado de terno, saiba que existe muito mais. Já fui <strong>jogador de futebol de categorias de base</strong> e tive <strong>banda de rock</strong>.</p>
                        <p>A música e o esporte me ensinaram sobre <strong>ritmo, criatividade e trabalho em equipe</strong>. A vida me ensinou que a rigidez quebra, mas a flexibilidade criativa constrói soluções onde ninguém mais vê saída.</p>
                    </BentoItem>

                    {/* D. A ESSÊNCIA (2x1 - WIDE) */}
                    <BentoItem 
                        className="md:col-span-2 md:row-span-1 bg-gradient-to-r from-green-900/40 to-[#1E293B]"
                        title="A Essência"
                        icon="fa-leaf"
                        subtitle="Vegano & Ativista"
                        accentColor="border-green-500"
                        scrollable={true}
                    >
                        <p>A busca pela verdade não se limita ao direito; ela me levou à minha transformação mais profunda: o <strong>veganismo</strong>. Foi uma árdua batalha contra a cultura e os hábitos com os quais fui criado, passando por um longo processo de vegetarianismo até romper totalmente com velhos padrões.</p>
                        <p>Isso demonstra minha essência: procuro viver com <strong>valores tradicionais cristãos</strong>, e entendi que a compaixão não deve ter barreiras. Não tenho medo da mudança; se o que faço hoje se provar errado amanhã, eu mudo. Minha resiliência está em buscar o <strong>aperfeiçoamento constante</strong>, sem aceitar o conformismo.</p>
                    </BentoItem>

                    {/* E. MEUS FIÉIS COMPANHEIROS (3x1 PANORÂMICO - EXPANDIDO) */}
                    <BentoItem 
                        className="md:col-span-3 md:row-span-1 min-h-[450px]" 
                        title="Meus Fiéis Companheiros"
                        subtitle="Família Multiespécie"
                        icon="fa-paw"
                        slideshow={dogPhotos.length > 0 ? dogPhotos : FALLBACK_PHOTOS} 
                        interval={1500} 
                        accentColor="border-orange-400"
                        alignBottom={true} 
                    >
                        {dogPhotosDiag?.mode === 'fallback' ? (
                            <div className="mb-3 text-[10px] font-bold uppercase tracking-wider bg-black/40 border border-white/20 rounded-lg px-3 py-2 inline-flex items-center gap-2">
                                <span>Fotos (fallback)</span>
                                <span className="opacity-80">{dogPhotosDiag.reason}</span>
                            </div>
                        ) : null}
                        <p className="text-lg font-medium text-white drop-shadow-md">Acima de tudo, minha conexão com a vida é visceral. Desde a mais tenra idade, os animais são <strong>meus irmãos</strong>.</p>
                        <p className="mt-4 text-white/90 drop-shadow-md">Essa irmandade moldou minha ética: para mim, o Direito Animal não é uma causa distante ou uma moda, é uma extensão da minha família e da minha fé.</p>
                    </BentoItem>

                    {/* F. MANIFESTO (1x1 VERTICAL - RESTAURADO INTEGRAL) */}
                    <div className="md:col-span-1 md:row-span-1 bg-[#C5A059] rounded-3xl p-8 flex flex-col justify-center items-center text-center relative overflow-hidden group cursor-default shadow-lg">
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20"></div>
                        <div className="relative z-10 flex flex-col items-center h-full justify-between">
                            <div>
                                <i className="fas fa-quote-left text-[#20243B] text-2xl mb-4 opacity-50"></i>
                                {/* Aumento de fonte para text-xl e restauração da frase completa */}
                                <h3 className="text-xl font-serif font-bold text-[#20243B] italic leading-relaxed px-1">
                                    "Se um único animal tiver que morrer para que eu sobreviva, deixe-me morrer, pois a minha vida não é mais importante que a dele."
                                </h3>
                            </div>
                            <div className="mt-6 flex items-center justify-center gap-2">
                                <span className="h-px w-6 bg-[#20243B]/50"></span>
                                {/* Restauração do nome completo */}
                                <span className="text-[10px] font-bold text-[#20243B] uppercase tracking-widest">Rodrigo Demeterco</span>
                                <span className="h-px w-6 bg-[#20243B]/50"></span>
                            </div>
                        </div>
                    </div>

                </div>

                <MissionValues />

            </div>
        </div>
    );
};

export default AboutMe;